import axios from 'axios';
import dayjs from 'dayjs';
import { setGlobal, getGlobal } from 'reactn';
import defaultGlobalState from '../defaultGlobalState';

const API_URL = process.env.REACT_APP_API_URL;

const handleExpiredToken = () => {
  setGlobal(defaultGlobalState);
  // localStorage.removeItem('jwt');
};

export const getServiceAccountDetails = async ({
  accountNumber,
  customerCode,
}) => {
  const summaryData = await getAccountSummary(accountNumber);

  const pastPaymentData = await getPastPayments(
    accountNumber,
    dayjs().subtract(1, 'year').format('YYYYMMDD'),
    dayjs().format('YYYYMMDD')
  );

  const meterData = await getMeterRead(accountNumber);

  const consumptionData = await getConsumptionHistory(
    accountNumber,
    dayjs().subtract(2, 'year').format('YYYYMMDD'),
    dayjs().format('YYYYMMDD')
  );

  const billingHistory = await getBillingHistory(
    accountNumber,
    dayjs().subtract(1, 'year').format('YYYYMMDD'),
    dayjs().format('YYYYMMDD')
  );

  const eBillStatus = await getEbillStatus(accountNumber, customerCode);

  return {
    summaryData,
    pastPaymentData,
    meterData,
    consumptionData,
    billingHistory,
    eBillStatus,
  };
};

export const getServiceAccount = async ({ accountNumber, customerCode }) => {
  try {
    const { data } = await axios.get(
      `${API_URL}/user-svc-accounts?accountNumber=${accountNumber}&customerCode=${customerCode}`
    );
    return data;
  } catch (error) {
    return error.response?.data;
  }
};

export const getServiceAccounts = async () => {
  // get list of service accounts user has added to their profile
  const { data: accountIds } = await axios.get(
    `${API_URL}/accounts/multi-prop-find`
  );

  // get details on those service accounts
  const serviceAccounts = await Promise.all(
    accountIds.map(async (userAccountId) => {
      try {
        const data = await getServiceAccount({
          accountNumber: userAccountId.account_no,
          customerCode: userAccountId.customer_code,
        });

        return {
          data,
        };
      } catch (err) {
        if (err.response.status === 401) {
          handleExpiredToken();
          return err.response;
        } else return err.response;
      }
    })
  );

  // filter out undefined items
  return serviceAccounts.filter((a) => !!a);
};

export const getAccountSummary = async (accountNumber) => {
  try {
    const accountSummary = await axios.get(
      `${API_URL}/user-account-summary?accountNumber=${accountNumber}`
    );
    return accountSummary.data;
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

export const getServiceOrder = async (accountNumber) => {
  try {
    const svcOrder = await axios.get(
      `${API_URL}/service-order?accountNumber=${accountNumber}`
    );
      return svcOrder.data;
    } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

export const findAccount = async (accountNo, customerCode) => {
  try {
    const res = await axios.get(
      `${API_URL}/user-svc-accounts?accountNumber=${accountNo}&customerCode=${customerCode}`
    );
    if (!res.error) {
      setGlobal({ FoundAccount: res.data });
      return res.data;
    }
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

export const getPastPayments = async (accountNumber, fromDate, toDate) => {
  toDate = dayjs().format('YYYYMMDD');
  fromDate = dayjs().subtract(1, 'year').format('YYYYMMDD');
  try {
    const res = await axios.get(
      `${API_URL}/past-payments?accountNumber=${accountNumber}&fromDate=${fromDate}&toDate=${toDate}`
    );
    return res.data.data;
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

export const submitMeterRead = async (accountNumber, meterData) => {
  try {
    const meterResponse = await axios.post(`${API_URL}/meter-reads`, meterData);
    return meterResponse.data;
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

export const getMeterRead = async (accountNumber) => {
  try {
    const meterResponse = await axios.get(
      `${API_URL}/meter-reads?accountNumber=${accountNumber}`
    );
    setGlobal({ currentMeterRead: meterResponse.data });
    return meterResponse.data;
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

export const submitVacationBilling = async (vacayData) => {
  try {
    await axios.post(`${API_URL}/vacation_billing_submissions/`, vacayData);
    return true;
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

export const submitMovingRequest = async (movingData) => {
  try {
    await axios.post(`${API_URL}/moving_request_submissions/`, movingData);
    return true;
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

export const getConsumptionHistory = async (
  accountNumber,
  fromDate,
  toDate
) => {
  try {
    const consumptionHistory = await axios.get(
      `${API_URL}/consumption-history?accountNumber=${accountNumber}&fromDate=${fromDate}&toDate=${toDate}`
    );
    return consumptionHistory.data.data;
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

export const updateEBill = async (accountNumber, customerCode, eBillFlag) => {
  const eBillData = {
    accountNumber,
    customerCode,
    eBillFlag,
  };
  try {
    const eBillResponse = await axios.post(
      `${API_URL}/update-ebill`,
      eBillData
    );
    await fetchCurrentAccount();

    return eBillResponse.data;
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

export const submitBillingInfo = async (accountNumber, routingNumber, bankingAccountNumber, name, bankAccountType) => {
  try {
    const submutBillingInfoResponse = await axios.get(
      `${API_URL}/autoPay?accountNumber=${accountNumber}&routingNumber=${routingNumber}&bankingAccountNumber=${bankingAccountNumber}&name=${name}&bankAccountType=${bankAccountType}`
    );
    await fetchCurrentAccount();
    return submutBillingInfoResponse.data;
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
}

export const getEbillStatus = async (accountNumber, customerCode) => {
  try {
    const status = await axios.get(
      `${API_URL}/ebill-status?accountNumber=${accountNumber}&customerCode=${customerCode}`
    );

    return status.data;
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

export const getBillingHistory = async (accountNumber, dateFrom, dateTo) => {
  try {
    const billingHistory = await axios.get(
      `${API_URL}/billing-history?accountNumber=${accountNumber}&dateFrom=${dateFrom}&dateTo=${dateTo}`
    );

    return billingHistory.data;
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

export const setDefaultProperty = async (accountNo) => {
  try {
    await axios.post(`${API_URL}/accounts/set-default-property/${accountNo}`);
    await fetchCurrentAccount();
    return true;
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

// added email to parameters and query URL
export const addProperty = async (accountNo, customerCode, email) => {
  try {
    const res = await axios.post(
      `${API_URL}/accounts/add-svc-account?ACCOUNT=${accountNo}&CUST_CODE=${customerCode}&EMAIL=${email}`
    );
    if (!res.error) {
      const serviceAccounts = await getServiceAccounts();
      setGlobal({ serviceAccounts });
      return true;
    }
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

export const removeSvcAccount = async (accountNumber, customerCode) => {
  try {
    const res = await axios.delete(
      `${API_URL}/accounts/remove-property?accountNumber=${accountNumber}&customerCode=${customerCode}`
    );
    if (!res.error) {
      return true;
    }
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

export const getSpeedpayKey = async () => {
  try {
    const key = await axios.get(`${API_URL}/speedpay-key`);
    if (!key.error) {
      return key.data;
    }
  } catch (error) {
    if (error.response.status === 401) {
      handleExpiredToken();
      return error.response.data;
    } else return error.response.data;
  }
};

// fetch account data for the currently selected account and update state accordingly
export const fetchCurrentAccount = async () => {
  const { selectedServiceAccountIndex, serviceAccounts } = getGlobal();

  // in case state does not yet have list of serviceAccounts
  if (!serviceAccounts) {
    return;
  }

  const selectedServiceAccount = serviceAccounts[selectedServiceAccountIndex];

  const accountNumber = selectedServiceAccount?.data?.ACCOUNT;
  const customerCode = selectedServiceAccount?.data?.CUST_CODE;

  // make sure both accountNumber and customerCode are available
  if (accountNumber && customerCode) {
    // get basic service account info (/api/serviceAccounts)
    const serviceAccount = await getServiceAccount({
      accountNumber,
      customerCode,
    });
    // get additional service account information
    const serviceAccountDetails = await getServiceAccountDetails({
      accountNumber,
      customerCode,
    });

    // construct new array of all service accounts
    const updateServiceAccounts = [...serviceAccounts];
    // update selected account with new information
    updateServiceAccounts[selectedServiceAccountIndex] = {
      data: serviceAccount,
      ...serviceAccountDetails,
    };
    // set the updated serviceAccount array as state
    setGlobal({ serviceAccounts: updateServiceAccounts });
  }
};

// add methods to object to handle warning no-anonymous-default-export
const serviceAccountObject  = {
  getServiceAccount,
  getServiceAccounts,
  getServiceOrder,
  getServiceAccountDetails,
  fetchCurrentAccount,
  findAccount,
  getPastPayments,
  submitMeterRead,
  getMeterRead,
  submitVacationBilling,
  submitMovingRequest,
  submitBillingInfo,
  setDefaultProperty,
  getConsumptionHistory,
  addProperty,
  updateEBill,
  getBillingHistory,
  getEbillStatus,
  getAccountSummary,
  getSpeedpayKey,
  removeSvcAccount,
};

export default serviceAccountObject
